/**
 * Transport — session bootstrap and the dialogue endpoint. Owns idempotency
 * ids, retry/backoff, and the response-shape guard. Gameplay code calls
 * these functions and never touches fetch or tokens.
 *
 * Guest identity (US 08.00): an anonymous Supabase session whose tokens live
 * in localStorage. Clearing browser data starts a fresh guest — by design.
 */
import { worldBus } from '@shared/bus.js';
import { newActionId } from '@shared/envelope.js';
import { ConnectionTracker } from './connection.js';
import { log, reportError } from './observability.js';

/** This tab's game-session identity — the session lock key (US 15.05). */
const SESSION_ID = crypto.randomUUID();

const URL_BASE = import.meta.env.VITE_SUPABASE_URL ?? '';
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const STORE_KEY = 'wca_session';

interface StoredSession {
  access_token: string;
  refresh_token: string;
  expires_at: number; // epoch seconds
}

export type ApiFailure = {
  ok: false;
  error: string;
  message: string;
  retryable: boolean;
  /** Present on INVENTORY_FULL: what fits vs. what the prompt must name. */
  manifest?: {
    wouldDestroy: { itemId: string; qty: number }[];
    fits: { itemId: string; qty: number }[];
  };
};
export type ApiResult<T> = { ok: true; result: T } | ApiFailure;

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function storeSession(s: StoredSession): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(s));
}

async function authRequest(path: string, body: unknown): Promise<StoredSession | null> {
  const res = await fetch(`${URL_BASE}/auth/v1/${path}`, {
    method: 'POST',
    headers: { apikey: ANON_KEY, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  const json = (await res.json()) as {
    access_token?: string;
    refresh_token?: string;
    expires_at?: number;
  };
  if (!json.access_token || !json.refresh_token) return null;
  const session: StoredSession = {
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    expires_at: json.expires_at ?? Math.floor(Date.now() / 1000) + 3000,
  };
  storeSession(session);
  return session;
}

/**
 * Returns a valid access token, creating an anonymous session on first play
 * and refreshing an expiring one. Null means auth is unavailable (e.g.
 * anonymous sign-ins disabled server-side) — the UI shows a plain notice.
 */
export async function ensureSession(): Promise<string | null> {
  if (!URL_BASE || !ANON_KEY) return null;
  const current = readSession();
  const now = Math.floor(Date.now() / 1000);
  if (current && current.expires_at - 60 > now) return current.access_token;
  if (current) {
    const refreshed = await authRequest('token?grant_type=refresh_token', {
      refresh_token: current.refresh_token,
    });
    if (refreshed) return refreshed.access_token;
  }
  const fresh = await authRequest('signup', {}); // anonymous sign-in (US 08.00)
  return fresh?.access_token ?? null;
}

export type DialogueOp =
  | { op: 'interact'; npcId: string }
  | { op: 'choose'; npcId: string; choiceId?: string; confirmOverflow?: 'destroy' | 'leave' }
  | { op: 'sync'; claim?: boolean }
  | { op: 'trash'; itemId: string; qty: number }
  | { op: 'consume'; itemId: string }
  | { op: 'collect_pending'; npcId: string }
  | { op: 'collect_drops' }
  | { op: 'journal' }
  | { op: 'travel'; landmarkId: string }
  | { op: 'hint'; tier: 1 | 2 | 3 }
  | { op: 'apartment' }
  | { op: 'album' }
  | { op: 'store_item'; itemId: string; qty: number }
  | { op: 'withdraw_item'; itemId: string; qty: number }
  | { op: 'buy_tile'; floor: number; x: number; y: number; kind: 'indoor' | 'garden' }
  | {
      op: 'place_furniture';
      floor: number;
      furnitureId: string;
      x: number;
      y: number;
      rotation: 0 | 90 | 180 | 270;
    }
  | { op: 'remove_furniture'; furnitureId: string }
  | { op: 'build_staircase'; floor: number; x: number; y: number }
  | { op: 'remove_staircase'; floor: number }
  | { op: 'hazard_hit'; hazardId: string }
  | { op: 'fishing_start'; spotId: string }
  | {
      op: 'fishing_submit';
      sessionId: string;
      catches: { fishIndex: number; catchTimeMs: number; hookDepth: number }[];
      confirmOverflow?: 'destroy' | 'leave';
    }
  | { op: 'ephemeral'; data: Record<string, unknown> };

const STATE_CHANGING = new Set([
  'choose',
  'trash',
  'consume',
  'collect_pending',
  'collect_drops',
  'travel',
  'hint',
  'store_item',
  'withdraw_item',
  'buy_tile',
  'place_furniture',
  'remove_furniture',
  'build_staircase',
  'remove_staircase',
  'hazard_hit',
  'fishing_submit',
]);

/** Shared tier tracker; main.ts bridges its events onto the world bus. */
export const connection = new ConnectionTracker();
setInterval(() => connection.poll(), 500); // tiers escalate with time

/**
 * Calls the dialogue function. State-changing ops carry ONE actionId across
 * every retry — that constancy is the whole idempotency contract (rule 3) —
 * and retry INDEFINITELY with capped backoff: a queued trade survives the
 * whole outage and replays on reconnect (US 10.02). Read-only ops give up
 * quickly; the caller simply asks again.
 */
export async function callDialogue<T = unknown>(
  payload: DialogueOp,
  opts: { keepalive?: boolean; actionId?: string } = {},
): Promise<ApiResult<T>> {
  const token = await ensureSession();
  if (!token) {
    return {
      ok: false,
      error: 'UNAUTHENTICATED',
      message: 'No session available',
      retryable: false,
    };
  }
  const stateChanging = STATE_CHANGING.has(payload.op);
  // Callers may pin the id — the overflow confirm flow re-sends the SAME
  // actionId with confirmOverflow set (106 §2).
  const actionId = stateChanging ? (opts.actionId ?? newActionId()) : undefined;
  const maxAttempts = stateChanging ? Number.POSITIVE_INFINITY : 3;

  let lastFailure: ApiFailure = {
    ok: false,
    error: 'SERVER_ERROR',
    message: 'Unreachable',
    retryable: true,
  };
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await fetch(`${URL_BASE}/functions/v1/dialogue`, {
        method: 'POST',
        headers: {
          apikey: ANON_KEY,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ actionId, sessionId: SESSION_ID, payload }),
        ...(opts.keepalive ? { keepalive: true } : {}),
      });
      const json = (await res.json()) as Record<string, unknown>;
      if (json.ok === true) {
        connection.reportSuccess();
        return { ok: true, result: json.result as T };
      }
      // Session lock (US 15.05): another tab took over. Freeze this one —
      // no retries, no further writes; the overlay owns the tab from here.
      if (json.error === 'SESSION_LOCKED') {
        worldBus.emit('session-locked', {});
      }
      lastFailure = {
        ok: false,
        error: typeof json.error === 'string' ? json.error : 'SERVER_ERROR',
        message: typeof json.message === 'string' ? json.message : '',
        retryable: Boolean(json.retryable),
        ...(json.manifest
          ? { manifest: json.manifest as NonNullable<ApiFailure['manifest']> }
          : {}),
      };
      // A typed refusal is a HEALTHY connection saying no.
      connection.reportSuccess();
      if (!lastFailure.retryable) return lastFailure;
    } catch (err) {
      connection.reportFailure();
      if (attempt === 1) {
        reportError(err, {
          ...(actionId ? { actionId } : {}),
          msg: `dialogue ${payload.op} attempt ${attempt}`,
        });
      }
    }
    // Backoff: 250ms → 500ms → 1s cap. Same actionId every time.
    await new Promise((r) => setTimeout(r, Math.min(1000, 250 * 2 ** (attempt - 1))));
  }
  log({
    level: 'warn',
    msg: 'dialogue call exhausted retries',
    ...(actionId ? { actionId } : {}),
    ctx: { op: payload.op },
  });
  return lastFailure;
}

/**
 * Client-owned state flush (US 10.03): every 5s while playing, and with
 * keepalive on tab-hide so the write survives the page going away.
 */
export function flushEphemeral(data: Record<string, unknown>, keepalive = false): void {
  void callDialogue({ op: 'ephemeral', data }, { keepalive });
}
